/**
 * Continue with Google.
 *
 * Google has already proved the address, so this door skips the
 * verify-your-email round trip the password form has to do. It asks the API
 * for the consent URL rather than building one here, because the client id and
 * the redirect belong to the server.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

export function GoogleAuthButton({ label = 'Continue with Google', onError }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/api/auth/google/start');
      window.location.assign(data.auth_url);
    } catch (err) {
      setBusy(false);
      onError?.(err.response?.data?.error || 'Google sign-in is unavailable right now.');
    }
  };
  // Google's own button colours in both themes: white with dark text, and its
  // dark variant (#131314 / #E3E3E3 / #8E918F). text-foreground on the white
  // button went pale in dark mode and the label could not be read.
  return (
    <button type="button" onClick={go} disabled={busy}
      className="flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-border bg-white text-[15.5px] font-medium text-[#1F1F1F] transition-colors hover:bg-muted/40 disabled:opacity-60 dark:border-[#8E918F] dark:bg-[#131314] dark:text-[#E3E3E3] dark:hover:bg-[#1E1F20]">
      {busy ? <Loader2 className="size-4 animate-spin" /> : (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
          <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
          <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
          <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
        </svg>
      )}
      <span>{label}</span>
    </button>
  );
}

export function OrRule({ text = 'or use your email' }) {
  return (
    <div className="flex items-center gap-3.5">
      <span className="h-px flex-1 bg-border" />
      <span className="text-[12.5px] text-muted-foreground">{text}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

export default GoogleAuthButton;
